<!--
  =====================================================================
  NEWS PAGE - NEWS SECTION LISTING
  =====================================================================
  
  Displays a list of company news articles with carousel and grid layouts.
  
  FEATURES:
  - Loads all news content from store
  - Filters sections to show only news-specific layouts (carousel, grid)
  - Supports dynamic component rendering
  - Shows loading and error states
  
  RENDERED COMPONENTS:
  - SectionNewsCarousel: Featured news carousel
  - SectionNewsGrid: News article grid
  - SectionCenter: Generic centered sections
  - SectionLeftTitle: Left-aligned sections with titles
  - HtmlTextSection: Custom HTML content sections
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />

    <main class="pagina">
      <div v-if="state.loading">Caricamento...</div>
      
      <div v-else-if="state.error">Errore durante il caricamento delle news</div>
      
      <div v-else>
        <component
          v-for="(section, index) in filteredSections"
          :key="index"
          :is="getComponent(section.type)"
          :content="section.content"
        />
      </div>
    </main>

    <ArrowUp /> 
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { state, loadPageContent, getNewsListSections } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// NEWS-SPECIFIC SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionNewsCarousel from '@/components/sections/news/SectionNewsCarousel.vue'
import SectionNewsGrid from '@/components/sections/news/SectionNewsGrid.vue'

// ───────────────────────────────────────────────────────────────────
// GENERIC SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import HtmlTextSection from '@/components/sections/html/SectionHtmlText.vue'

// ───────────────────────────────────────────────────────────────────
// UTILITIES
// ───────────────────────────────────────────────────────────────────
import ArrowUp from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Filters page sections to include only news-specific layouts
 * Uses store helper function to get properly formatted news sections
 * 
 * @returns {Array} Array of news sections with carousel and grid types
 */
const filteredSections = computed(() => {
  return getNewsListSections()
})

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize page on component mount
 * Loads all news content from store
 */
onMounted(async () => {
  await loadPageContent('/news')
})

// ───────────────────────────────────────────────────────────────────
// COMPONENT RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Maps section types to their corresponding Vue components
 * 
 * @param {string} type - The section type from page content
 * @returns {VueComponent} The component to render
 */
function getComponent(type) {
  switch (type) {
    case 'news-carousel': 
      return SectionNewsCarousel
    case 'news-grid': 
      return SectionNewsGrid
    case 'html-text': 
      return HtmlTextSection
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/main';

.pagina {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  @include flex-gap(2rem, column);

  @media (max-width: 768px) {
    padding: 1rem;
  }
}
</style>